import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { Check, RotateCcw, X, ZoomIn, ZoomOut } from 'lucide-react';
import ProfilePhotoFrame from './ProfilePhotoFrame';
import { DEFAULT_FRAMING } from './photoFraming';

export default function ProfilePhotoEditor({ session, onSave, onClose, darkTheme }) {
  const [framing, setFraming] = useState(session.framing);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [conflict, setConflict] = useState(false);
  const [height, setHeight] = useState(window.innerHeight);
  const saving = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    const resize = () => setHeight(window.innerHeight);
    window.addEventListener('resize', resize);
    return () => { mounted.current = false; window.removeEventListener('resize', resize); };
  }, []);
  const title = session.target === 'avatar' ? 'Adjust profile photo' : 'Adjust cover photo';
  const aspect = session.target === 'avatar' ? 1 : session.aspect;
  const changeZoom = (event) => {
    const zoom = Number(event.currentTarget.value);
    setFraming((previous) => ({ ...previous, zoom }));
  };
  const save = async () => {
    if (saving.current || !ready || failed || conflict) return;
    saving.current = true; setBusy(true); setError('');
    try { await onSave(session, framing); }
    catch (failure) {
      if (!mounted.current) return;
      const stale = failure?.response?.status === 409;
      setConflict(stale);
      setError(stale ? 'This photo changed. Close and reopen Adjust photo.' :
        session.replaced ? 'Your new photo is saved. Position was not saved. Try again.' :
          'Position was not saved. Try again.');
    } finally {
      saving.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  return <Dialog open onClose={() => { if (!saving.current) onClose(); }}
    aria-labelledby="profile-photo-editor-title" maxWidth={false}
    PaperProps={{ className: `profile-photo-editor ${darkTheme ? 'profile-photo-editor--dark' : ''}` }}>
    <header className="profile-photo-editor__header">
      <h2 id="profile-photo-editor-title">{title}</h2>
      <button type="button" className="profile-photo-icon" aria-label="Close photo adjustment" title="Close"
        disabled={busy} onClick={onClose}><X size={20} /></button>
    </header>
    <div className="profile-photo-editor__stage">
      <ProfilePhotoFrame source={session.sourceUrl} framing={framing}
        alt={session.target === 'avatar' ? 'profile photo' : 'cover photo'}
        className={session.target === 'avatar' ? 'profile-photo-editor__avatar' : 'profile-photo-editor__cover'}
        style={{ aspectRatio: String(aspect), maxWidth: Math.max(100, (height - 230) * aspect) }}
        onChange={setFraming} disabled={busy || conflict}
        onReady={() => setReady(true)} onError={() => { setFailed(true); setReady(false); }}>
        {session.target === 'cover' && session.overlap && <div className="profile-photo-editor__overlap"
          aria-hidden="true" style={session.overlap}>
          {session.avatarUrl && <img src={session.avatarUrl} alt="" draggable={false} />}
        </div>}
      </ProfilePhotoFrame>
    </div>
    {failed && <p role="alert" className="profile-photo-editor__error">This photo cannot be displayed. Choose a supported image.</p>}
    {error && <p role="alert" className="profile-photo-editor__error">{error}</p>}
    <div className="profile-photo-editor__zoom">
      <ZoomOut size={18} aria-hidden="true" />
      <label className="profile-photo-sr-only" htmlFor="profile-photo-zoom">Zoom</label>
      <input id="profile-photo-zoom" type="range" min="1" max="3" step="0.01" value={framing.zoom}
        disabled={!ready || failed || busy || conflict}
        onInput={changeZoom} onChange={changeZoom} />
      <ZoomIn size={18} aria-hidden="true" />
      <output htmlFor="profile-photo-zoom">{Math.round(framing.zoom * 100)}%</output>
    </div>
    <footer className="profile-photo-editor__footer">
      <button type="button" className="profile-photo-icon" aria-label="Reset photo position" title="Reset"
        disabled={busy || !ready || conflict} onClick={() => setFraming(DEFAULT_FRAMING)}><RotateCcw size={18} /></button>
      <div className="profile-photo-editor__commands">
        <button type="button" disabled={busy} onClick={onClose}>Cancel</button>
        <button type="button" className="profile-photo-editor__save" disabled={!ready || failed || busy || conflict}
          onClick={save}><Check size={18} aria-hidden="true" />{busy ? 'Saving...' : 'Save'}</button>
      </div>
    </footer>
  </Dialog>;
}
